import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { api, formatApiErrorDetail } from "@/lib/api";
import { getVisitorId } from "@/lib/visitor";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { MapPin, ShareNetwork, FilePdf, PlayCircle, QrCode, Clock, Heart } from "@phosphor-icons/react";
import { toast } from "sonner";

// Fix default leaflet marker icon for webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function youtubeEmbed(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([\w-]{11})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return url;
}

function Md({ children, dropcap = false }) {
  if (!children) return null;
  return (
    <div className={"md-body" + (dropcap ? " with-dropcap" : "")}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{children}</ReactMarkdown>
    </div>
  );
}

function readingTime(text) {
  const words = (text || "").split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.round(words / 200));
  return `${minutes} min read`;
}

function initialsOf(name) {
  return (name || "").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "•";
}

function SectionRenderer({ section, onImageClick }) {
  if (!section) return null;
  const video = youtubeEmbed(section.video_url);
  return (
    <div className="reveal">
      {section.content && <Md>{section.content}</Md>}
      {section.gallery?.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8, marginTop: 16 }}>
          {section.gallery.map((src, i) => (
            <button key={i} onClick={() => onImageClick && onImageClick(src)}
              style={{ padding: 0, border: 0, background: "transparent", cursor: "zoom-in" }}>
              <img alt="" src={src} style={{ width: "100%", height: 160, objectFit: "cover", border: "1px solid var(--theme-border, var(--border))" }} />
            </button>
          ))}
        </div>
      )}
      {video && (
        <div style={{ marginTop: 16, position: "relative", paddingBottom: "56.25%", height: 0, border: "1px solid var(--theme-border, var(--border))" }}>
          <iframe src={video} title="video" allow="encrypted-media; picture-in-picture; fullscreen"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }} />
        </div>
      )}
      {section.pdfs?.length > 0 && (
        <div style={{ marginTop: 16 }}>
          {section.pdfs.map((p, i) => (
            <a key={i} href={p.url} target="_blank" rel="noreferrer" data-testid={`section-pdf-${i}`}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", border: "1px solid var(--theme-border, var(--border))", marginBottom: 8, textDecoration: "none" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <FilePdf size={18} weight="duotone" color="var(--theme-accent, var(--danger))" /> {p.name}
              </span>
              <span className="overline theme-secondary">Download</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function OverviewTab({ page, onImageClick }) {
  return (
    <div>
      {page.facts?.length > 0 && (
        <div data-testid="public-facts" className="theme-surface reveal" style={{ padding: 20, border: "1px solid", marginBottom: 28 }}>
          <div className="overline" style={{ marginBottom: 12 }}>Quick facts</div>
          <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
            {page.facts.map((f, i) => <li key={i}>{f}</li>)}
          </ul>
        </div>
      )}
      {page.full_description && (
        <section style={{ marginTop: 8 }} className="reveal">
          <h2 className="font-display" style={{ fontSize: "1.8rem", fontWeight: 800, marginTop: 8, marginBottom: 12 }}>About</h2>
          <Md dropcap>{page.full_description}</Md>
        </section>
      )}
      {page.gallery?.length > 0 && (
        <section style={{ marginTop: 32 }} className="reveal">
          <h2 className="font-display" style={{ fontSize: "1.6rem", fontWeight: 800 }}>Gallery</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8, marginTop: 16 }}>
            {page.gallery.map((src, i) => (
              <button key={i} onClick={() => onImageClick(src)}
                style={{ padding: 0, border: 0, background: "transparent", cursor: "zoom-in" }}
                data-testid={`gallery-image-${i}`}>
                <img alt="" src={src} style={{ width: "100%", height: 180, objectFit: "cover", border: "1px solid var(--theme-border, var(--border))", transition: "transform .2s" }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.02)"}
                  onMouseLeave={(e) => e.currentTarget.style.transform = "none"} />
              </button>
            ))}
          </div>
        </section>
      )}
      {page.video_url && (
        <section style={{ marginTop: 32 }}>
          <h2 className="font-display" style={{ fontSize: "1.6rem", fontWeight: 800 }}>
            <PlayCircle size={22} weight="duotone" style={{ verticalAlign: "middle" }} /> Video
          </h2>
          <div style={{ marginTop: 16, position: "relative", paddingBottom: "56.25%", height: 0, border: "1px solid var(--theme-border, var(--border))" }}>
            <iframe src={youtubeEmbed(page.video_url)} title="video" allow="encrypted-media; picture-in-picture; fullscreen"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }} />
          </div>
        </section>
      )}
      {page.pdfs?.length > 0 && (
        <section style={{ marginTop: 32 }}>
          <h2 className="font-display" style={{ fontSize: "1.6rem", fontWeight: 800 }}>Documents</h2>
          <div style={{ marginTop: 12 }}>
            {page.pdfs.map((p, i) => (
              <a key={i} href={p.url} target="_blank" rel="noreferrer" data-testid={`public-pdf-${i}`}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", border: "1px solid var(--theme-border, var(--border))", marginBottom: 8, textDecoration: "none" }}>
                <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <FilePdf size={20} weight="duotone" color="var(--theme-accent, var(--danger))" /> {p.name}
                </span>
                <span className="overline theme-secondary">Download</span>
              </a>
            ))}
          </div>
        </section>
      )}
      {page.faq?.length > 0 && (
        <section style={{ marginTop: 32 }} data-testid="public-faq">
          <h2 className="font-display" style={{ fontSize: "1.6rem", fontWeight: 800 }}>Questions & answers</h2>
          <div style={{ marginTop: 16 }}>
            {page.faq.map((q, i) => (
              <details key={i} className="theme-border-top" style={{ padding: "16px 0" }}>
                <summary style={{ cursor: "pointer", fontWeight: 600, fontSize: 16, listStyle: "none" }}>
                  {q.q}
                </summary>
                <div style={{ marginTop: 10 }}><Md>{q.a}</Md></div>
              </details>
            ))}
          </div>
        </section>
      )}
      {(page.latitude != null && page.longitude != null) && (
        <section style={{ marginTop: 32 }}>
          <h2 className="font-display" style={{ fontSize: "1.6rem", fontWeight: 800 }}>Location</h2>
          <div style={{ marginTop: 16, height: 360 }}>
            <MapContainer center={[page.latitude, page.longitude]} zoom={13} style={{ height: "100%" }}>
              <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <Marker position={[page.latitude, page.longitude]}>
                <Popup>{page.title}</Popup>
              </Marker>
            </MapContainer>
          </div>
        </section>
      )}
    </div>
  );
}

export default function PublicPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [err, setErr] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [likes, setLikes] = useState(0);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    api.get(`/public/pages/${slug}`)
      .then(({ data }) => {
        setPage(data);
        setLikes(data.likes || 0);
        setLiked(localStorage.getItem(`liked_${data.slug}`) === "1");
        // Fire-and-forget visit tracking
        api.post("/track", {
          path: `/p/${data.slug}`, slug: data.slug,
          visitor_id: getVisitorId(),
          referrer: document.referrer || "",
        }).catch(() => {});
      })
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Not found"));
  }, [slug]);

  const doLike = async () => {
    if (liked) return;
    try {
      const { data } = await api.post(`/public/pages/${page.slug}/like`, { visitor_id: getVisitorId() });
      setLikes(data.likes);
      setLiked(true);
      localStorage.setItem(`liked_${page.slug}`, "1");
      if (!data.already_liked) toast.success("Thanks for the like!");
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => { if (e.key === "Escape") setLightbox(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  const share = async () => {
    const shareData = { title: page.title, text: page.short_description, url: window.location.href };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch { /* dismissed */ }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied");
    }
  };

  if (err) return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24, textAlign: "center" }}>
      <div>
        <div className="overline">Not found</div>
        <h1 className="font-display" style={{ fontSize: 48, fontWeight: 900, letterSpacing: "-0.03em" }}>404</h1>
        <p style={{ color: "var(--text-2)" }}>{err}</p>
        <Link to="/" className="btn">Go home</Link>
      </div>
    </div>
  );
  if (!page) return <div style={{ padding: 80, textAlign: "center", color: "var(--text-2)" }}>Loading…</div>;

  const themeClass = `theme-${page.theme || "classic"} theme-scope`;
  const sections = page.sections || [];
  const hasSections = sections.length > 0;

  return (
    <div className={themeClass} style={{ minHeight: "100vh" }} data-testid="public-page">
      {/* Top mini bar */}
      <div className="theme-surface" style={{ position: "sticky", top: 0, backdropFilter: "blur(8px)", borderBottom: "1px solid", zIndex: 20 }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 24px" }}>
          <Link to="/" data-testid="public-home-link"
            style={{ display: "flex", gap: 8, alignItems: "center", textDecoration: "none", color: "inherit" }}>
            <div className="theme-accent-bg" style={{ width: 24, height: 24, display: "grid", placeItems: "center" }}>
              <QrCode size={14} color="#fff" weight="bold" />
            </div>
            <span className="font-display" style={{ fontWeight: 800, fontSize: 14, letterSpacing: "-0.02em" }}>7AuraDigital</span>
          </Link>
          <button onClick={share} className="btn btn-sm" data-testid="public-share-btn"><ShareNetwork size={14} /> Share</button>
        </div>
      </div>
      {/* Hero */}
      {page.featured_image && <img alt={page.title} src={page.featured_image} className="hero-img" />}

      <div style={{ maxWidth: 980, margin: "0 auto", padding: "40px 24px 80px" }}>
        {page.category && <div className="overline theme-accent-text" style={{ marginBottom: 12 }}>{page.category}</div>}
        <h1 className="hero-title" style={{ fontSize: "clamp(40px, 7vw, 76px)", margin: 0 }} data-testid="public-page-title">
          {page.title}
        </h1>
        {page.subtitle && <p className="theme-secondary" style={{ fontSize: 22, marginTop: 16, fontFamily: "'Fraunces', ui-serif, Georgia, serif", fontStyle: "italic" }}>{page.subtitle}</p>}

        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginTop: 22 }}>
          {(page.city || page.state || page.country) && (
            <span className="chip" data-testid="public-location-chip">
              <MapPin size={14} weight="duotone" /> {[page.city, page.state, page.country].filter(Boolean).join(", ")}
            </span>
          )}
          {page.full_description && (
            <span className="chip" data-testid="public-reading-time">
              <Clock size={14} weight="duotone" /> {readingTime(page.full_description)}
            </span>
          )}
        </div>

        {page.short_description && (
          <div style={{ fontSize: 19, marginTop: 28, lineHeight: 1.65, fontFamily: "'Fraunces', ui-serif, Georgia, serif" }}><Md>{page.short_description}</Md></div>
        )}

        {/* Tab selector — always visible above content when there are sections */}
        {hasSections && (
          <div className="tab-list" style={{ marginTop: 36 }} data-testid="public-tabs">
            <button className={"tab-card" + (activeTab === 0 ? " active" : "")}
              onClick={() => setActiveTab(0)} data-testid="public-tab-0">
              Overview
            </button>
            {sections.map((s, i) => (
              <button key={i + 1} className={"tab-card" + (activeTab === i + 1 ? " active" : "")}
                onClick={() => setActiveTab(i + 1)} data-testid={`public-tab-${i + 1}`}>
                {s.name || `Section ${i + 1}`}
              </button>
            ))}
          </div>
        )}

        <div style={{ marginTop: 32 }} data-testid={`public-tab-content-${activeTab}`}>
          {!hasSections || activeTab === 0 ? (
            <OverviewTab page={page} onImageClick={setLightbox} />
          ) : (
            <SectionRenderer section={sections[activeTab - 1]} onImageClick={setLightbox} />
          )}
        </div>

        {page.tags?.length > 0 && (
          <div style={{ marginTop: 40, display: "flex", gap: 6, flexWrap: "wrap" }}>
            {page.tags.map((t) => <span key={t} className="badge badge-muted">#{t}</span>)}
          </div>
        )}

        {/* Like row (above author card) */}
        <div style={{ marginTop: 36, display: "flex", alignItems: "center", gap: 12 }} data-testid="public-like-row">
          <button onClick={doLike} disabled={liked} data-testid="public-like-btn"
            style={{
              display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 18px",
              border: "1px solid var(--theme-border, var(--border))",
              background: liked ? "var(--theme-accent, #C2410C)" : "transparent",
              color: liked ? "#fff" : "var(--theme-text, var(--text))",
              cursor: liked ? "default" : "pointer",
              fontFamily: "'Cabinet Grotesk', sans-serif", fontWeight: 700, fontSize: 15,
              borderRadius: 999, transition: "all .2s",
            }}>
            <Heart size={18} weight={liked ? "fill" : "regular"} />
            {liked ? "Liked" : "Like this page"}
          </button>
          <span data-testid="public-like-count" style={{ fontFamily: "'Fraunces', ui-serif, Georgia, serif", fontSize: 18, fontWeight: 600 }}>
            {likes} {likes === 1 ? "like" : "likes"}
          </span>
        </div>

        {/* Author byline */}
        {page.author_name && (
          <div className="author-card reveal" data-testid="public-author-card">
            <div className="author-avatar">{initialsOf(page.author_name)}</div>
            <div>
              <div className="overline theme-secondary" style={{ fontSize: 10, marginBottom: 2 }}>Created by</div>
              <div className="font-display" style={{ fontWeight: 800, fontSize: 18, letterSpacing: "-0.01em" }}>
                {page.author_name}
              </div>
              {page.author_role && (
                <div className="theme-secondary" style={{ fontSize: 13, fontFamily: "'Fraunces', ui-serif, Georgia, serif", fontStyle: "italic" }}>
                  {page.author_role}
                </div>
              )}
            </div>
          </div>
        )}

        <footer className="theme-border-top theme-secondary" style={{ marginTop: 60, paddingTop: 24, fontSize: 12, textAlign: "center" }}>
          Powered by <Link to="/" style={{ fontWeight: 700 }}>7AuraDigital</Link>
        </footer>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="lightbox" onClick={() => setLightbox(null)} data-testid="public-lightbox">
          <button className="lightbox-close" onClick={() => setLightbox(null)} aria-label="Close">×</button>
          <img alt="" src={lightbox} onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
